'use strict';

import { queryAll, hasClass } from '../domUtil';
import {
    EVENT_KEY_LEFT,
    EVENT_KEY_RIGHT,
    EVENT_KEY_UP,
    EVENT_KEY_DOWN,
    EVENT_KEY_HOME,
    EVENT_KEY_END
} from '../constants';

/**
 * Ensure exactly one active/tabbable radio in a group.
 *
 * @param {HTMLElement|Document} root
 * @param {string} radioSelector - e.g. '.size-btn[role="radio"]'
 * @param {Object} config
 * @param {string} config.selectedClass - class indicating selected state
 * @param {string} [config.notAvailableClass] - class indicating a non-selectable / not-available option
 */
export function normalizeRadioGroup(root, radioSelector, {
    selectedClass,
    notAvailableClass
} = {}) {
    const container = root || document;
    const radios = queryAll(radioSelector, container) || [];
    if (!radios.length) return;

    let current = radios.find(btn => hasClass(btn, selectedClass));
    if (!current && notAvailableClass) {
        current = radios.find(btn => !hasClass(btn, notAvailableClass)) || radios[0];
    } else if (!current) {
        current = radios[0];
    }

    radios.forEach(btn => {
        const isActive = btn === current;
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
        btn.tabIndex = isActive ? 0 : -1;
    });
}

/**
 * Arrow/Home/End keyboard navigation for radio-like controls.
 *
 * @param {string} itemSelector - selector for the individual radio items
 * @param {string[]} groupSelectors - optional container selectors to scope the group
 */
export function initRadioGroupKeyboardNav(itemSelector, groupSelectors = []) {
    const { body } = document;

    const isNavigable = el => {
        if (!el) return false;
        if (el.hidden) return false;
        if (el.getAttribute('aria-hidden') === 'true') return false;
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        if (!el.getClientRects().length) return false;
        return true;
    };

    body.addEventListener('keydown', e => {
        const { key } = e;
        if (![EVENT_KEY_RIGHT, EVENT_KEY_DOWN, EVENT_KEY_LEFT, EVENT_KEY_UP, EVENT_KEY_HOME, EVENT_KEY_END].includes(key)) {
            return;
        }

        const current = e.target.closest(itemSelector);
        if (!current || !body.contains(current)) return;

        let group = null;
        for (const sel of groupSelectors) {
            group = current.closest(sel);
            if (group) break;
        }
        if (!group) group = document;

        const radios = (queryAll(itemSelector, group) || []).filter(isNavigable);
        if (!radios.length) return;

        const index = radios.indexOf(current);
        if (index === -1) return;

        let newIndex = index;
        if (key === EVENT_KEY_RIGHT || key === EVENT_KEY_DOWN) {
            newIndex = (index + 1) % radios.length;
            e.preventDefault();
        } else if (key === EVENT_KEY_LEFT || key === EVENT_KEY_UP) {
            newIndex = (index - 1 + radios.length) % radios.length;
            e.preventDefault();
        } else if (key === EVENT_KEY_HOME) {
            newIndex = 0;
            e.preventDefault();
        } else if (key === EVENT_KEY_END) {
            newIndex = radios.length - 1;
            e.preventDefault();
        }
        if (newIndex === index) return;

        radios.forEach((r, idx) => {
            const isSelected = idx === newIndex;
            r.setAttribute('aria-checked', isSelected ? 'true' : 'false');
            r.tabIndex = isSelected ? 0 : -1;
        });

        const target = radios[newIndex];
        target.focus();
        target.click();
    });

    body.addEventListener('click', e => {
        const current = e.target.closest(itemSelector);
        if (!current || !body.contains(current)) return;

        let group = null;
        for (const sel of groupSelectors) {
            group = current.closest(sel);
            if (group) break;
        }
        if (!group) group = document;

        const radios = (queryAll(itemSelector, group) || []).filter(isNavigable);
        if (!radios.length) return;

        const index = radios.indexOf(current);
        if (index === -1) return;

        radios.forEach((r, idx) => {
            const isSelected = idx === index;
            r.setAttribute('aria-checked', isSelected ? 'true' : 'false');
            r.tabIndex = isSelected ? 0 : -1;
        });
    });
}
